import React, { useEffect, useState } from 'react';
import { motion, type Variants } from 'framer-motion';
import {
  getNotificationPreferences,
  updateNotificationPreferences,
  type NotificationPreferences,
} from '../api/notifications';

const DEFAULTS: NotificationPreferences = {
  push_enabled: true,
  analysis_complete: true,
  tips: true,
  promotions: false,
  account: true,
  quiet_hours_start: null,
  quiet_hours_end: null,
};

type PrefKey = 'analysis_complete' | 'tips' | 'promotions' | 'account';

const CATEGORY_ROWS: { key: PrefKey; label: string; hint: string }[] = [
  { key: 'analysis_complete', label: 'Analysis ready', hint: "When your skin, hair or makeup analysis is done." },
  { key: 'tips', label: 'Tips & guidance', hint: 'Skincare tips and getting the most from your results.' },
  { key: 'promotions', label: 'Offers & promotions', hint: 'Credit deals and plan offers. Off by default.' },
  { key: 'account', label: 'Account & security', hint: 'Credits, billing and security alerts.' },
];

/** Notification preferences card for the profile page. Self-contained: loads and
 * saves via the notifications API and animates like the surrounding sections. */
const NotificationSettings: React.FC<{ variants?: Variants }> = ({ variants }) => {
  const [prefs, setPrefs] = useState<NotificationPreferences>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    void getNotificationPreferences()
      .then((p) => { if (alive) setPrefs({ ...DEFAULTS, ...p }); })
      .catch(() => { /* keep defaults on load failure */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => {
      alive = false;
    };
  }, []);

  const persist = async (next: NotificationPreferences) => {
    setPrefs(next);
    setSaving(true);
    try {
      const saved = await updateNotificationPreferences(next);
      setPrefs({ ...DEFAULTS, ...saved });
    } catch {
      /* keep optimistic value */
    } finally {
      setSaving(false);
    }
  };

  const toggle = (key: keyof NotificationPreferences) => {
    void persist({ ...prefs, [key]: !prefs[key] });
  };

  const setQuiet = (key: 'quiet_hours_start' | 'quiet_hours_end', value: string) => {
    const num = value === '' ? null : Math.max(0, Math.min(23, Number.parseInt(value, 10)));
    void persist({ ...prefs, [key]: Number.isNaN(num) ? null : num });
  };

  const Checkbox: React.FC<{ checked: boolean; onChange: () => void; label: string; hint: string; disabled?: boolean }> = ({
    checked,
    onChange,
    label,
    hint,
    disabled,
  }) => (
    <label
      aria-label={label}
      style={{
        display: 'flex',
        gap: 'var(--s-3)',
        cursor: disabled ? 'default' : 'pointer',
        alignItems: 'flex-start',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} className="sr-only" />
      <span className={`kv-checkbox${checked ? ' kv-checkbox--on' : ''}`} aria-hidden="true">
        <svg viewBox="0 0 12 10" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width={10} height={9}>
          <path d="M1 5l3 3 7-7" />
        </svg>
      </span>
      <span className="min-w-0">
        <span className="t-small" style={{ display: 'block', fontWeight: 500 }}>{label}</span>
        <span className="t-label t-mute" style={{ display: 'block', marginTop: 2 }}>{hint}</span>
      </span>
    </label>
  );

  return (
    <motion.section variants={variants} className="card card--bordered card--flat">
      <div className="stack-4">
        <div className="row-4" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h3 className="t-h3" style={{ margin: 0 }}>Notifications</h3>
          {saving && <span className="t-label t-mute">Saving…</span>}
        </div>

        <Checkbox
          checked={prefs.push_enabled}
          onChange={() => { toggle('push_enabled'); }}
          label="Push notifications"
          hint="Master switch for browser push. Account & security alerts are always delivered."
          disabled={loading}
        />

        <div style={{ height: 1, background: 'var(--rule, rgba(31,36,33,.08))' }} />

        {CATEGORY_ROWS.map((row) => (
          <Checkbox
            key={row.key}
            checked={prefs[row.key]}
            onChange={() => { toggle(row.key); }}
            label={row.label}
            hint={row.hint}
            disabled={loading || (!prefs.push_enabled && row.key !== 'account')}
          />
        ))}

        <div className="stack-2">
          <span className="t-small" style={{ fontWeight: 500 }}>Quiet hours</span>
          <span className="t-label t-mute">Pause tips & offers during these local hours (0–23). Leave blank to disable.</span>
          <div style={{ display: 'flex', gap: 'var(--s-3)', alignItems: 'center' }}>
            <input
              type="number"
              min={0}
              max={23}
              placeholder="From"
              aria-label="Quiet hours start (0–23)"
              value={prefs.quiet_hours_start ?? ''}
              onChange={(e) => { setQuiet('quiet_hours_start', e.target.value); }}
              disabled={loading}
              className="form-input"
              style={{ width: 90 }}
            />
            <span className="t-small t-mute">to</span>
            <input
              type="number"
              min={0}
              max={23}
              placeholder="To"
              aria-label="Quiet hours end (0–23)"
              value={prefs.quiet_hours_end ?? ''}
              onChange={(e) => { setQuiet('quiet_hours_end', e.target.value); }}
              disabled={loading}
              className="form-input"
              style={{ width: 90 }}
            />
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default NotificationSettings;
